import _ from 'lodash';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import parse, { DomElement } from 'html-react-parser';
import css from './ScriptRenderer.scss';

class ScriptManager {
	scripts = new Map<string, DomElement>([]);

	addScript = (domNode: DomElement) => {
		this.scripts.set(uuidv4(), domNode);
	};

	loadScript = ({ attrs, innerHTML, targetNode }) => {
		const script = document.createElement('script');

		_.forOwn(attrs, (value, key) => {
			script.setAttribute(key, value);

			if (['async', 'defer'].includes(key)) {
				script[key] = ['', 'true'].includes(value);
			}
		});

		if (!attrs.src && innerHTML) {
			script.innerHTML = `(function() {${innerHTML}})()`;
		}

		targetNode.appendChild(script);
	};

	clear() {
		this.scripts.clear();
	}
}

type Props = {
	value: string; // html|script string
	disabled?: boolean;
	scriptOnly?: boolean; // parse only valid javascript and html script tag
};

export class ScriptRenderer extends React.PureComponent<Props> {
	ScriptManager: ScriptManager = new ScriptManager();

	scriptsRef = React.createRef<HTMLDivElement>();

	componentDidMount() {
		this.renderScripts();
	}

	componentDidUpdate(prevProps: any, prevState: any, snapshot) {
		if (this.getDecodedValue() !== this.getDecodedValue(prevProps.value)) {
			this.renderScripts();
		}
	}

	getDecodedValue = (value = this.props.value) => {
		return decodeURI(value);
	};

	clearScriptHTML = () => {
		if (this.scriptsRef.current) {
			this.scriptsRef.current.innerHTML = '';
		}
	};

	renderScripts() {
		const { scripts, loadScript } = this.ScriptManager;

		// clear
		this.clearScriptHTML();

		// add scripts
		scripts.forEach(script => {
			const isText = script.type === 'text';
			const attrs = script.attribs || {};
			const innerHTML = isText ? script.data : _.get(script.children, '0.data');

			loadScript({
				attrs,
				innerHTML,
				targetNode: this.scriptsRef.current,
			});
		});
	}

	renderHTML = html =>
		parse(html, {
			replace: (domNode: DomElement) => {
				if (domNode.type === 'script') {
					this.ScriptManager.addScript(domNode);
					return <></>; // replace script with an empty fragment
				}

				if (
					this.props.scriptOnly &&
					domNode.type === 'text' &&
					typeof domNode.data === 'string' &&
					domNode.data.trim() &&
					domNode.parent === null
				) {
					this.ScriptManager.addScript(domNode);
					return <></>; // replace script with an empty fragment
				}

				if (this.props.scriptOnly) {
					return <></>; // replace all with empty fragment to avoid render other html markup
				}

				return false;
			},
		});

	render() {
		const value = this.getDecodedValue();
		this.ScriptManager.clear();

		if (!value || this.props.disabled) {
			return null;
		}

		return (
			<>
				<div className={css.scripts} ref={this.scriptsRef} />
				{value ? this.renderHTML(value) : null}
			</>
		);
	}
}
